import { unwrapResult } from '@reduxjs/toolkit'
import { Col, Dropdown, DropDownProps, Input, Row } from 'antd'
import React, {
  forwardRef,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { RootDispatch } from '../../../app/store'
import { ThemeContext } from '../../../theme/ThemeContext'
import { sleep } from '../../../utils/SystemUtils'

export interface RecipeItemInputRef {
  focus: () => void
  select: () => void
}

const ColumnsPerRow = 4


interface ListItemProps {
  active: boolean
  code: string
  onSelect: (code: string) => void
}

const ListItem = ({ active, code, onSelect }: ListItemProps): ReactElement => {
  const theme = useContext(ThemeContext)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (active) {
      ref.current?.scrollIntoView({ behavior: 'auto', block: 'nearest' })
    }
  }, [active])

  return (
    <Col
      ref={ref}
      span={24 / ColumnsPerRow}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onSelect(code)
      }}
      style={{
        color: active ? theme.tt : theme.tc1,
        fontSize: '1rem',
        textAlign: 'center',
        lineHeight: '2rem',
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
        padding: '0 5px',
        cursor: 'pointer',
        backgroundColor: active ? theme.c1 : undefined,
      }}
    >
      {code} 
    </Col>
  )
}

interface RecipeItemInputProps extends Pick<DropDownProps, 'getPopupContainer'> {
  dataList?: string[]
  value?: string | number
  onChange?: (value?: string) => void
  onBlur?: (v?: any) => void
  onPressEnter?: (v?: string) => void
  record?: any
  placeholderInput?: string
  style?: React.CSSProperties
  autoFocus?: boolean
}

const RealRecipeItemInput = (
  {
    dataList,
    value,
    onChange,
    onBlur,
    onPressEnter,
    style,
    autoFocus = false,
    getPopupContainer = (node) => node.parentElement || document.body,
    placeholderInput = '',
    ...others
  }: RecipeItemInputProps,
  ref: React.Ref<RecipeItemInputRef>
): ReactElement => {
  const theme = useContext(ThemeContext)
  const dispatch = useDispatch<RootDispatch>()
  const [isFocused, setIsFocused] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [selectedName, setSelectedName] = useState<string>('')
  const inputRef = useRef<Input>(null)

  useEffect(() => {
    if (isVisible) {
      setSelectedName(dataList?.[0] || '')
    }
  }, [isVisible])

  // 下拉内容
  const overlay = (
    <Col style={{ overflow: 'hidden' }}>
      <Row style={{ width: '36rem', maxHeight: '12rem', overflowY: 'auto' }}>
        {dataList?.map((str) => (
          <ListItem
            key={str}
            code={str}
            active={selectedName === str}
            onSelect={(item) => {
              onChange && onChange(item)
              setIsVisible(false)
              setSelectedName(item)
              inputRef.current?.focus()
            }}
          />
        ))}
      </Row>
      <div
        style={{
          height: '2rem',
          lineHeight: '2rem',
          color: theme.c1,
          textAlign: 'center',
          fontWeight: 'bold',
          backgroundColor: theme.c3,
          cursor: 'pointer',
        }}
        onClick={() => setIsVisible(false)}
      >
        关闭(ESC)
      </div>
    </Col>
  )

  const input = (
    <Input
      autoFocus={autoFocus}
      ref={inputRef}
      {...others}
      style={{ ...style, width: '400px', height: '2rem' }}
      onClick={() => setIsVisible(true)}
      placeholder={placeholderInput}
      onFocus={() => {
        setIsFocused(true)
        setIsVisible(true)
        if (dataList?.length && !selectedName) {
          setSelectedName(dataList[0])
        }
      }}
      onBlur={async () => {
        await sleep(250)
        setIsFocused(false)
        setIsVisible(false)
        onBlur?.(value)
      }}
      onChange={(e) => {
       return
      }}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (!selectedName || !dataList?.length) return

        let index = dataList.findIndex((c) => c === selectedName)
        if (index < 0) return

        switch (e.key) {
          case 'Escape':
            setIsVisible(false)
            break
          case 'ArrowRight':
            index = index + 1
            break
          case 'ArrowLeft':
            index = index - 1
            break
          case 'ArrowDown':
            index = index + ColumnsPerRow
            break
          case 'ArrowUp':
            index = index - ColumnsPerRow
            break
          default:
            return
        }

        if (index >= 0 && index < dataList?.length) {
          setSelectedName(dataList[index])
        }
      }}
      onPressEnter={(e) => {
        e.stopPropagation()
        if (selectedName) {
          onChange && onChange(selectedName)
          onPressEnter && onPressEnter(selectedName)
          inputRef.current?.focus()
        }
        setIsVisible(false)
      }}
    />
  )

  return (
    <div style={{ position: 'relative' }}>
      <Dropdown
        overlay={overlay}
        trigger={[]}
        visible={isVisible && !!dataList?.length}
        onVisibleChange={setIsVisible}
        getPopupContainer={getPopupContainer}
      >
        {input}
      </Dropdown>
    </div>
  )
}

export const RecipeItemInput = forwardRef(RealRecipeItemInput)